import React from 'react';
import { Task } from '../../../utils/taskParser';
import { Card } from '../../ui/card';
import { CheckCircle2, XCircle, Circle, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '../../../lib/utils';

interface TaskDisplayProps {
  tasks: Task[];
  title?: string;
  className?: string;
}

export const TaskDisplay: React.FC<TaskDisplayProps> = ({ tasks, title, className }) => {
  const [expandedTasks, setExpandedTasks] = React.useState<Set<string>>(new Set());

  const toggleExpanded = (taskId: string) => {
    setExpandedTasks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const getStatusIcon = (status: Task['status']) => {
    switch (status) {
      case 'complete':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'canceled':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'incomplete':
      default:
        return <Circle className="w-5 h-5 text-gray-400" />;
    }
  };

  if (tasks.length === 0) {
    return null;
  }

  return (
    <div className={cn("space-y-3", className)}>
      {title && (
        <div className="text-sm font-medium text-gray-600 mb-2">
          {title}
        </div>
      )}
      
      <div className="space-y-2">
        {tasks.map((task) => {
          const isExpanded = expandedTasks.has(task.id);
          const hasNotes = task.notes && task.notes.trim().length > 0;
          
          return (
            <Card 
              key={task.id} 
              className={cn(
                "p-3 transition-all duration-200",
                task.status === 'complete' && "bg-gray-50/50",
                task.status === 'canceled' && "bg-red-50/30"
              )}
            >
              <div className="space-y-2">
                {/* Header row */}
                <div className="flex items-start gap-3">
                  {/* Status icon */}
                  <div className="flex-shrink-0 mt-0.5">
                    {getStatusIcon(task.status)}
                  </div>
                  
                  {/* Main content */}
                  <div className="flex-1 min-w-0">
                    {/* Title and expand button */}
                    <div className="flex items-start gap-2">
                      <div className="flex-1">
                        <div className={cn(
                          "font-medium",
                          task.status === 'complete' && "line-through text-gray-500",
                          task.status === 'canceled' && "line-through text-red-700/70"
                        )}>
                          {task.title}
                        </div>
                      </div>
                      
                      {/* Expand button if has notes */}
                      {hasNotes && (
                        <button
                          onClick={() => toggleExpanded(task.id)}
                          className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors"
                          aria-label={isExpanded ? "Collapse notes" : "Expand notes"}
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-500" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-500" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Notes section (expandable) */}
                {hasNotes && isExpanded && (
                  <div className="ml-8 mt-2 pt-2 border-t border-gray-100">
                    <div className="text-sm text-gray-600 whitespace-pre-wrap font-mono bg-gray-50 p-2 rounded">
                      {task.notes}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};